/**
 * MachineTranslationBadge
 * -----------------------
 * Inline disclosure shown wherever non-English content is rendered.
 *
 * - English readers see nothing (null return).
 * - Amber state ("AI-translated"): Claude Sonnet 4.5 produced the text under
 *   the strict unabridged protocol but no human has reviewed it yet.
 * - Emerald state ("Translation"): a human has signed off the translation.
 *
 * Designed to be elegant rather than alarming — the goal is honest provenance
 * without scaring the reader away from the localised text.  A collapsible
 * "Learn more" reveals the full disclosure on demand.
 */
import React, { useState } from 'react';
import { Languages, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';

const LANG_DISPLAY = {
  en: 'English', ru: 'Russian', uk: 'Ukrainian', pt: 'Portuguese',
  fr: 'French', it: 'Italian', es: 'Spanish', ko: 'Korean',
  zh: 'Mandarin (Simplified)', 'zh-TW': 'Mandarin (Traditional)',
};

export default function MachineTranslationBadge({
  language,
  machineTranslated,
  className = '',
  testId,
}) {
  const [expanded, setExpanded] = useState(false);
  if (!language || language === 'en') return null;

  const langName = LANG_DISPLAY[language] || language.toUpperCase();
  const isAmber = !!machineTranslated;

  const tone = isAmber
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-emerald-50 border-emerald-200 text-emerald-900';
  const Icon = isAmber ? AlertTriangle : CheckCircle2;
  const iconTone = isAmber ? 'text-amber-700' : 'text-emerald-700';
  const dotTone = isAmber ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div
      className={`rounded-lg border text-[11px] leading-snug ${tone} ${className}`}
      data-testid={testId || 'machine-translation-badge'}
    >
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-black/[0.03] rounded-lg transition-colors"
        data-testid={`${testId || 'machine-translation-badge'}-toggle`}
      >
        <span className="relative flex h-2 w-2 flex-shrink-0">
          {isAmber && (
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dotTone} opacity-60`} />
          )}
          <span className={`relative inline-flex h-2 w-2 rounded-full ${dotTone}`} />
        </span>
        <Icon className={`w-3 h-3 flex-shrink-0 ${iconTone}`} />
        <span className="flex-1 font-semibold tracking-wide">
          <Languages className="w-3 h-3 inline-block mr-1 -mt-0.5 opacity-70" />
          {isAmber ? 'AI-translated' : 'Translated'} · {langName}
          {isAmber && <span className="ml-1.5 text-[10px] font-normal opacity-70">· unrevised</span>}
        </span>
        <ChevronRight
          className={`w-3 h-3 flex-shrink-0 opacity-50 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>
      {expanded && (
        <div className="px-3 pb-2.5 pt-1 border-t border-current/10 text-[11px]">
          {isAmber ? (
            <>
              <p>
                This text was auto-translated from the English original by Claude Sonnet 4.5 under a strict
                unabridged protocol: every paragraph, every list item, every heading.
              </p>
              <p className="mt-1 opacity-80">
                A human translator has not yet reviewed it. The <strong>English version remains the canonical
                source</strong> if any phrasing is unclear.
              </p>
            </>
          ) : (
            <p>
              Translated from the English original and reviewed for clinical accuracy. The English version
              remains the canonical source.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
